/** @jsxImportSource @emotion/react */
import { css, keyframes } from "@emotion/react";
import { useEffect, useRef, useState } from "react";
import { ReactComponent as CheckIcon } from "../../assets/icons/checkCircleFilled.svg";
import { ReactComponent as ErrorIcon } from "../../assets/icons/xCircleRed.svg";
import {
  COOL_GREY,
  DARK_GREY,
  LIGHT_GREY,
  MIDDLE_GREY,
  WHITE,
} from "../../styles/colors";
import ErrorView from "../ErrorView";
import { BoxedErrorInverse } from "../../forms/elements/boxedMessage/boxedMessageStyles";
import * as Buttons from "../../forms/elements/buttons";
import { getResource } from "../../language/resources/getResource";

export type BulkOperationProgress = {
  current: number;
  total: number;
  successCount: number;
  failureCount: number;
  isComplete?: boolean;
  isCancelled?: boolean;
};

type Props = {
  resources: Record<string, any>;
  visible: boolean;
  current: number;
  total: number;
  successCount?: number;
  failureCount?: number;
  title?: string;
  succeededTitle?: string;
  failedTitle?: string;
  itemTitle?: string;
  errorMessage?: string;
  showCancel?: boolean;
  showETA?: boolean;
  /**
   * Whether the run has settled. Defaults to "processed everything", which a run
   * that stops early never reaches — without it there is no Close button.
   */
  isComplete?: boolean;
  isCancelled?: boolean;
  isCancelling?: boolean;
  /**
   * Overrides for the shared cancellation copy. The defaults are written for a save,
   * where stopping means work not done; an irreversible operation has to say that what
   * already ran still stands, so it passes its own.
   */
  cancelledTitle?: string;
  cancelledMessage?: string;
  cancellingHint?: string;
  onClose?: () => void;
  onCancel?: () => void;
};

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const fadeCss = css`
  transition: opacity 0.4s ease;
  opacity: 1;
`;

const overlayCss = css`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const cardCss = css`
  background: ${WHITE};
  padding: 24px;
  width: 380px;
  border-radius: 10px;
  border: 1px solid ${LIGHT_GREY};
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.2);
  text-align: center;
  user-select: none;
`;

const titleCss = css`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
`;

const progressBarContainerCss = css`
  height: 10px;
  background: ${COOL_GREY};
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 20px;
`;

const progressBarFillCss = (percent: number) => css`
  height: 100%;
  width: ${percent}%;
  border-radius: 6px;

  background: linear-gradient(90deg, #0078d4, #4fa3f7, #0078d4);

  background-size: 200% 100%;
  animation: ${shimmer} 3s linear infinite;
`;

const progressTextCss = css`
  font-size: 16px;
  color: ${DARK_GREY};
`;

const statsCss = css`
  margin-top: 15px;
  font-size: 16px;
`;

const iconCss = css`
  width: 16px;
  height: 16px;
  margin-right: 6px;
  vertical-align: middle;
`;

const statItemCss = css`
  display: inline-flex;
  align-items: center;
  margin-right: 12px;
`;

const etaCss = css`
  margin-top: 8px;
  font-size: 11px;
  color: ${MIDDLE_GREY};
`;

const cancellationCss = css`
  margin-top: 20px;
  font-size: 14px;
  color: ${MIDDLE_GREY};
`;

export const BulkProgressCard = ({
  resources,
  visible,
  current,
  total,
  successCount = 0,
  failureCount = 0,
  title,
  itemTitle,
  errorMessage,
  showCancel,
  showETA = false,
  isComplete: isCompleteProp,
  isCancelled,
  isCancelling,
  cancelledTitle,
  cancelledMessage,
  cancellingHint,
  onClose,
  onCancel,
}: Props) => {
  const succeeded = getResource(resources, "BULK_PROGRESS_SUCCEEDED");
  const failed = getResource(resources, "BULK_PROGRESS_FAILED");
  const notProcessed = getResource(resources, "BULK_PROGRESS_NOT_PROCESSED");
  const [animatedPercent, setAnimatedPercent] = useState(0);
  const processedCount = current;
  const totalCount = total;
  const actualPercent =
    totalCount > 0 ? (processedCount / totalCount) * 100 : 0;

  const startTime = useRef(Date.now());

  const actualPercentRef = useRef(actualPercent);

  useEffect(() => {
    actualPercentRef.current = actualPercent;
  }, [actualPercent, processedCount]);

  useEffect(() => {
    // loop to animate progress (60 fps)
    const id = setInterval(() => {
      setAnimatedPercent((prev) => {
        // do nothing if at actual progress
        if (prev >= actualPercent) return prev;

        // move towards the target
        const diff = actualPercent - prev;
        const step = Math.max(diff * 0.08, 0.4);

        // do not go past actual progress
        return Math.min(prev + step, actualPercent);
      });
    }, 16); 

    return () => clearInterval(id);
  }, [actualPercent]);

  useEffect(() => {
    if (visible) {
      setAnimatedPercent(0);
      startTime.current = Date.now();
    }
  }, [visible]);

  // ETA calculation
  const elapsed = (Date.now() - startTime.current) / 1000;
  const rate = current / (elapsed || 1);
  const remaining = total - current;
  const eta = rate > 0 ? Math.round(remaining / rate) : 0;

  const isComplete = isCompleteProp ?? current >= total;
  const hasFailures = failureCount > 0;
  const isCancellingActive = isCancelling && !isCancelled;
  const displayPercent = isComplete ? 100 : animatedPercent;
  const notProcessedCount = Math.max(total - (successCount + failureCount), 0);

  const displayTitle = isCancellingActive
    ? getResource(resources, "BULK_PROGRESS_CANCELLING")
    : isCancelled
      ? (cancelledTitle ?? getResource(resources, "BULK_PROGRESS_CANCELLED"))
      : isComplete
        ? hasFailures
          ? getResource(resources, "BULK_PROGRESS_COMPLETED_WITH_FAILURES")
          : getResource(resources, "BULK_PROGRESS_COMPLETED_SUCCESSFULLY")
        : title;

  if (!visible) return null;

  return (
    <div css={overlayCss}>
      <div css={[cardCss, fadeCss]}>
        {/* Title */}
        <div css={titleCss}>{displayTitle}</div>

        {/* Progress Bar - visible while running */}
        {!isComplete && !isCancelled && (
          <>
            <div css={progressBarContainerCss}>
              <div css={progressBarFillCss(displayPercent)} />
            </div>

            <div css={progressTextCss}>
              {current} / {total} {itemTitle} ({Math.round(displayPercent)}%)
            </div>
          </>
        )}

        {/* Success / Failure stats */}
        <div css={statsCss}>
          <span css={statItemCss}>
            <CheckIcon css={iconCss} />
            {successCount} {succeeded}
          </span>

          <span css={statItemCss}>
            <ErrorIcon css={iconCss} />
            {failureCount} {failed}
          </span>
        </div>

        {/* Anything the run never reached, however it stopped. Zero on a run that
            processed everything, so this stays hidden on the normal path. */}
        {(isComplete || isCancelled) && notProcessedCount > 0 && (
          <span
            css={css`
              ${statItemCss};
              margin-top: 5px;
            `}
          >
            {notProcessedCount} {notProcessed}
          </span>
        )}

        {isCancelled && (
          <div css={cancellationCss}>
            {cancelledMessage ??
              getResource(
                resources,
                "BULK_PROGRESS_CANCELLED_MESSAGE",
                itemTitle ?? getResource(resources, "BULK_PROGRESS_REQUESTS"),
              )}
          </div>
        )}

        {isCancellingActive && (
          <div css={cancellationCss}>
            {cancellingHint ??
              getResource(resources, "BULK_PROGRESS_CANCELLING_HINT")}
          </div>
        )}

        {/* Error Summary */}
        {failureCount > 0 && (
          <div
            css={css`
              margin-top: 20px;
            `}
          >
            <ErrorView
              error={errorMessage}
              boxStyle={BoxedErrorInverse}
              rootCss={css`
                user-select: none;
                padding-top: 10px;
              `}
            />
          </div>
        )}

        {/* ETA */}
        {showETA && !isComplete && !isCancelled && (
          <div css={etaCss}>
            {current === 0
              ? getResource(resources, "BULK_PROGRESS_STARTING")
              : eta > 1
                ? getResource(resources, "BULK_PROGRESS_SECONDS_REMAINING", eta)
                : getResource(resources, "BULK_PROGRESS_ALMOST_DONE")}
          </div>
        )}

        {/* Completion message */}
        {isComplete && !hasFailures && (
          <div css={etaCss}>{getResource(resources, "BULK_PROGRESS_DONE")}</div>
        )}

        {(isComplete || isCancelled) && (
          <Buttons.SmallNeutral
            css={css`
              margin-top: 20px;
            `}
            onClick={onClose}
          >
            {getResource(resources, "BULK_PROGRESS_CLOSE")}
          </Buttons.SmallNeutral>
        )}

        {!isComplete && showCancel && !isCancelled && !isCancelling && (
          <Buttons.SmallSecondary
            css={css`
              margin-top: 20px;
              margin-right: 10px;
            `}
            onClick={onCancel}
          >
            {getResource(resources, "BULK_PROGRESS_CANCEL")}
          </Buttons.SmallSecondary>
        )}
      </div>
    </div>
  );
};
